var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { Component, Emit, Inject, Prop, Vue, Watch, } from "vue-property-decorator";
import GrindingForm from "../../components/grindingform.vue";
import MaterialForm from "../../components/materialform.vue";
import ModalBase from "../../components/modalbase.vue";
import MultiplicityForm from "../../components/multiplicityform.vue";
import RotationForm from "../../components/dialog/rotationform.vue";
import ThicknessForm from "../../components/dialog/thicknessform.vue";
import { isManufacturablePart, isManufacturableSinglePart, isSinglePart, isPurchasedPart, } from "../utilities/documentdialog";
import { WorkStepType, } from "../utilities/wsi4_interfaces";
import { GrindingOption, isAllowedAdditionalProcess, isGrindingOption, isMaterialWithGas, isMultiplicity, } from "../utilities/articleeditdata";
import { objEq, clone, isInstanceOf, } from "../utilities/aux";
import { isValid, } from "../utilities/material";
import { isNumber, isArray, isBoolean, } from "../utilities/wsi4_typeguards";
function isCurrentRotation(arg) {
    const typeGuardMap = {
        type: (t) => t === "FixedRotation",
        value: (t) => isArray(t, isNumber),
        change: isBoolean,
    };
    return isInstanceOf(arg, typeGuardMap);
}
function isCurrentMaterial(arg) {
    const typeGuardMap = {
        type: (t) => t === "MaterialWithGas",
        value: isMaterialWithGas,
        change: isBoolean,
    };
    return isInstanceOf(arg, typeGuardMap);
}
function isCurrentThickness(arg) {
    const typeGuardMap = {
        type: (t) => t === "Thickness",
        value: isNumber,
        change: isBoolean,
    };
    return isInstanceOf(arg, typeGuardMap);
}
function isCurrentMultiplicity(arg) {
    const typeGuardMap = {
        type: (t) => t === "Multiplicity",
        value: isMultiplicity,
        change: isBoolean,
    };
    return isInstanceOf(arg, typeGuardMap);
}
function isCurrentGrinding(arg) {
    const typeGuardMap = {
        type: (t) => t === "MechanicalGrinding",
        value: isGrindingOption,
        change: isBoolean,
    };
    return isInstanceOf(arg, typeGuardMap);
}
function isCurrentValues(arg) {
    const typeGuardMap = {
        fixedRotations: isCurrentRotation,
        materialWithGas: isCurrentMaterial,
        thickness: isCurrentThickness,
        multiplicity: isCurrentMultiplicity,
        mechanicalGrinding: isCurrentGrinding,
    };
    return isInstanceOf(arg, typeGuardMap);
}
const emptyCurrentValues = {
    fixedRotations: {
        type: "FixedRotation",
        value: [],
        change: false,
    },
    materialWithGas: {
        type: "MaterialWithGas",
        value: {
            material: {
                identifier: "",
                name: "",
                description: "",
            },
            cuttingGasId: "",
            invalid: true,
        },
        change: false,
    },
    mechanicalGrinding: {
        type: "MechanicalGrinding",
        value: GrindingOption.none,
        change: false,
    },
    multiplicity: {
        type: "Multiplicity",
        value: 0,
        change: false,
    },
    thickness: {
        type: "Thickness",
        value: 0,
        change: false,
    },
};
const typeGuardMap = {
    fixedRotations: (t) => isArray(t, isNumber),
    materialWithGas: (t) => isMaterialWithGas(t),
    mechanicalGrinding: (t) => isGrindingOption(t),
    multiplicity: (t) => isMultiplicity(t),
    thickness: (t) => isNumber(t),
};
function validValue(c) {
    if (!c.change) {
        return true;
    }
    switch (c.type) {
        case "Multiplicity": {
            return isNumber(c.value) && c.value > 0;
        }
        case "Thickness": {
            return true;
        }
        case "MaterialWithGas": {
            return !c.value.invalid;
        }
        case "MechanicalGrinding": {
            return true;
        }
        case "FixedRotation": {
            return c.value.length === 0 || (Math.min(...c.value) >= 0 && (c.value[0] < 180.));
        }
    }
}
function wsi4Data(parts, steps, possibleSelections, currentValues, shopConfig) {
    const convert = (step, part) => {
        if (possibleSelections[step].every(s => s.vertexKey !== part.vertexKey)) {
            throw Error("Has to be possible");
        }
        switch (step) {
            case "fixedRotations": {
                if (!isManufacturablePart(part)) {
                    throw Error("Wrong part");
                }
                return [
                    {
                        vertexKey: part.vertexKeyOfSheetCutting,
                        attributes: {
                            fixedRotations: currentValues[step].value,
                        },
                        additionalProcesses: [],
                    },
                ];
            }
            case "materialWithGas": {
                if (!isManufacturablePart(part)) {
                    throw Error("Wrong part");
                }
                return [
                    {
                        vertexKey: part.vertexKey,
                        attributes: {
                            sheetMaterial: currentValues[step].value.material,
                        },
                        additionalProcesses: [],
                    },
                    {
                        vertexKey: part.vertexKeyOfSheetCutting,
                        attributes: {
                            cuttingGas: currentValues[step].value.cuttingGasId,
                        },
                        additionalProcesses: [],
                    },
                ];
            }
            case "mechanicalGrinding": {
                if (!isManufacturablePart(part)) {
                    throw Error("Wrong part");
                }
                if (!isAllowedAdditionalProcess("mechanicalGrinding", shopConfig)) {
                    throw new Error("Missing process");
                }
                return [
                    {
                        vertexKey: part.vertexKeyOfSheetCutting,
                        attributes: {},
                        additionalProcesses: [
                            {
                                process: "mechanicalDeburring",
                                addNode: currentValues[step].value === GrindingOption.doubleSidedMechanicalDeburring
                                    || currentValues[step].value === GrindingOption.singleSidedMechanicalDeburring,
                                userDataEntries: [
                                    {
                                        key: "deburrDoubleSided",
                                        value: currentValues[step].value === GrindingOption.doubleSidedMechanicalDeburring,
                                    },
                                ],
                            },
                            {
                                process: "slideGrinding",
                                addNode: currentValues[step].value === GrindingOption.slideGrinding,
                                userDataEntries: [],
                            },
                        ],
                    },
                ];
            }
            case "thickness": {
                if (!isManufacturableSinglePart(part)) {
                    throw Error("Wrong part");
                }
                return [
                    {
                        vertexKey: part.vertexKeyOfSheetCutting,
                        attributes: {
                            thickness: currentValues[step].value,
                        },
                        additionalProcesses: [],
                    },
                ];
            }
            case "multiplicity": {
                const value = currentValues[step].value;
                if (!isNumber(value)) {
                    throw Error("Wrong type.");
                }
                return [
                    {
                        vertexKey: part.vertexKey,
                        attributes: {
                            importMultiplicity: value,
                        },
                        additionalProcesses: [],
                    },
                ];
            }
        }
    };
    let vertices = [];
    for (const part of parts) {
        for (const step of steps) {
            if (!currentValues[step].change) {
                continue;
            }
            if (possibleSelections[step].some(p => p.vertexKey === part.vertexKey)) {
                vertices = [
                    ...vertices,
                    ...convert(step, part),
                ];
            }
        }
    }
    return vertices;
}
let QuickActionsDialog = class QuickActionsDialog extends Vue {
    constructor() {
        super(...arguments);
        this.currentStep = "multiplicity";
        this.currentValues = emptyCurrentValues;
        this.invalidValue = false;
    }
    close() { }
    index(step) {
        return this.types.findIndex(t => t === step);
    }
    isDisabled(step) {
        return this.possibleSelections[step].length === 0;
    }
    currentStepColor(step) {
        return !this.isDisabled(step) && this.currentStep === step;
    }
    alreadyDoneColor(step) {
        return !this.isDisabled(step) && this.currentValues[step].change;
    }
    stillToDoColor(step) {
        return !this.isDisabled(step) && !this.currentStepColor(step) && !this.alreadyDoneColor(step);
    }
    invalidColor(step) {
        return !this.isDisabled(step) && !this.valid(step);
    }
    testId(s) {
        return s + "-step";
    }
    submit() {
        const data = wsi4Data(this.dialogData.selection, this.types, this.possibleSelections, this.currentValues, this.shopConfig);
        if (data.length === 0) {
            this.close();
            return;
        }
        if (!this.networkBroker.queueChangeAttributesRequest(data)) {
            return;
        }
        if (!this.networkBroker.queueGraphRequest()) {
            return;
        }
        this.networkBroker.process();
        this.close();
    }
    adaptMaterial(v) {
        if (this.currentStep !== "thickness") {
            return v;
        }
        if (this.types.some(t => t === "materialWithGas") && v.materialWithGas.change) {
            v.materialWithGas.value.invalid = !isValid(this.shopConfig, this.thicknesses(), v.materialWithGas.value.material.identifier, v.materialWithGas.value.cuttingGasId);
            return v;
        }
        else {
            return v;
        }
    }
    changeValue(value) {
        if (!typeGuardMap[this.currentStep](value)) {
            throw Error("Wrong type.");
        }
        const v = this.currentValues;
        v[this.currentStep].value = value;
        this.currentValues = this.adaptMaterial(v);
        this.invalidValue = this.computeInvalidValue();
    }
    value(step, vertexKey) {
        return this.possibleSelections[step].some(p => p.vertexKey === vertexKey) && this.currentValues[step].change ? this.currentValues[step].value : undefined;
    }
    thicknesses() {
        return this.possibleSelection.map(p => {
            if (!isManufacturablePart(p)) {
                throw Error("Wrong type.");
            }
            const v = this.value("thickness", p.vertexKey);
            if (v !== undefined) {
                if (!isNumber(v)) {
                    throw Error("Wrong type.");
                }
                return v;
            }
            return p.thickness;
        });
    }
    changeChange() {
        const v = this.currentValues;
        v[this.currentStep].change = !this.currentValues[this.currentStep].change;
        this.currentValues = this.adaptMaterial(v);
        this.invalidValue = this.computeInvalidValue();
    }
    onClicked(type) {
        if (this.isDisabled(type)) {
            return;
        }
        this.currentStep = type;
    }
    computeInvalidValue() {
        if (this.currentValues === undefined) {
            return true;
        }
        return this.types.some(t => {
            const v = this.currentValues[t];
            return v !== undefined && !validValue(v);
        });
    }
    valid(t) {
        if (this.currentValues === undefined) {
            return true;
        }
        return validValue(this.currentValues[t]);
    }
    get types() {
        const showMap = {
            fixedRotations: this.shopConfig.settings.fixedRotationsEnabled,
            materialWithGas: true,
            mechanicalGrinding: isAllowedAdditionalProcess("mechanicalGrinding", this.shopConfig),
            multiplicity: true,
            thickness: true,
        };
        const typesInOrder = [
            "multiplicity",
            "thickness",
            "materialWithGas",
            "fixedRotations",
            "mechanicalGrinding",
        ];
        return typesInOrder.filter(t => showMap[t]);
    }
    get nextStep() {
        const n = this.index(this.currentStep) + 1;
        return n === this.types.length ? undefined : this.types[n];
    }
    get changeText() {
        return {
            mechanicalGrinding: this.i18n.t("changeGrinding"),
            fixedRotations: this.i18n.t("changeFixedRotations"),
            materialWithGas: this.i18n.t("changeMaterial"),
            multiplicity: this.i18n.t("changeMultiplicity"),
            thickness: this.i18n.t("changeThickness"),
        };
    }
    get explanationText() {
        return {
            mechanicalGrinding: this.i18n.t("manufacturablePartExplanation"),
            fixedRotations: this.i18n.t("manufacturablePartExplanation"),
            materialWithGas: this.i18n.t("manufacturablePartExplanation"),
            multiplicity: this.i18n.t("singlePartExplanation"),
            thickness: this.i18n.t("thicknessPartExplanation"),
        };
    }
    get possibleSelections() {
        return {
            mechanicalGrinding: this.dialogData.selection.filter(a => isManufacturablePart(a) && a.mechanicalGrinding !== undefined),
            fixedRotations: this.dialogData.selection.filter(a => isManufacturablePart(a) && a.fixedRotations !== undefined),
            materialWithGas: this.dialogData.selection.filter(a => isManufacturablePart(a) && a.materialIdWithCuttingGasId !== undefined),
            multiplicity: this.dialogData.selection.filter(a => isSinglePart(a) && !isPurchasedPart(a)),
            thickness: this.dialogData.selection.filter(a => isManufacturableSinglePart(a) && a.lastWorkStepType !== WorkStepType.sheetBending),
        };
    }
    get possibleSelection() {
        return this.possibleSelections[this.currentStep].sort((a, b) => a.articleName.localeCompare(b.articleName));
    }
    get selectionComplement() {
        return this.dialogData.selection.filter(s => this.possibleSelection.every(p => p.vertexKey !== s.vertexKey))
            .sort((a, b) => a.articleName.localeCompare(b.articleName));
    }
    get defaultValues() {
        return {
            mechanicalGrinding: (() => {
                const numbers = this.possibleSelections["mechanicalGrinding"].reduce((n, e) => {
                    if (!isManufacturablePart(e)) {
                        throw Error("Wrong type.");
                    }
                    if (e.mechanicalGrinding === undefined) {
                        throw Error("Wrong type.");
                    }
                    n[e.mechanicalGrinding.option] += 1;
                    return n;
                }, {
                    none: 0,
                    singleSidedMechanicalDeburring: 0,
                    doubleSidedMechanicalDeburring: 0,
                    slideGrinding: 0,
                });
                return {
                    type: "MechanicalGrinding",
                    value: Object.keys(numbers)
                        .reduce((res, key) => {
                        if (!isGrindingOption(key)) {
                            throw Error("Not grinding option");
                        }
                        return numbers[key] > numbers[res] ? key : res;
                    }, GrindingOption.none),
                    change: false,
                };
            })(),
            fixedRotations: (() => {
                const numbers = this.possibleSelections["fixedRotations"].reduce((n, e) => {
                    if (!isManufacturablePart(e)) {
                        throw Error("Wrong type.");
                    }
                    const rotations = e.fixedRotations;
                    if (rotations === undefined) {
                        throw Error("Missing fixed rotations");
                    }
                    const f = n.findIndex(entry => objEq(entry.r, rotations.rotations));
                    if (f === -1) {
                        n.push({
                            r: rotations.rotations,
                            n: 1,
                        });
                    }
                    else {
                        ++n[f].n;
                    }
                    return n;
                }, [
                    {
                        r: [],
                        n: 0,
                    },
                ]);
                const entry = numbers.reduce((res, entry) => res.n > entry.n ? res : entry, numbers[0]);
                return {
                    type: "FixedRotation",
                    value: entry.r,
                    change: false,
                };
            })(),
            materialWithGas: (() => {
                const numbers = this.possibleSelections["materialWithGas"].reduce((n, e) => {
                    if (!isManufacturablePart(e)) {
                        throw Error("Wrong type.");
                    }
                    const m = n.findIndex(entry => entry.m === e.materialIdWithCuttingGasId.sheetMaterialId && entry.c === e.materialIdWithCuttingGasId.cuttingGasId);
                    if (m === -1) {
                        n.push({
                            m: e.materialIdWithCuttingGasId.sheetMaterialId,
                            c: e.materialIdWithCuttingGasId.cuttingGasId,
                            n: 1,
                        });
                    }
                    else {
                        n[m].n += 1;
                    }
                    return n;
                }, []);
                if (numbers.length === 0) {
                    throw Error("Missing Material entries");
                }
                const materialEntry = numbers.reduce((res, entry) => res.n > entry.n ? res : entry, numbers[0]);
                const m = this.shopConfig.material.find(m => m.identifier === materialEntry.m);
                if (m === undefined) {
                    throw Error("Material missing in shopConfig");
                }
                return {
                    type: "MaterialWithGas",
                    value: {
                        material: m,
                        cuttingGasId: materialEntry.c,
                        invalid: false,
                    },
                    change: false,
                };
            })(),
            multiplicity: (() => {
                const numbers = this.possibleSelections["multiplicity"].reduce((n, e) => {
                    const f = n.findIndex(entry => entry.m === e.multiplicity);
                    if (f === -1) {
                        n.push({
                            m: e.multiplicity,
                            n: 1,
                        });
                    }
                    else {
                        ++n[f].n;
                    }
                    return n;
                }, []);
                if (numbers.length === 0) {
                    return {
                        type: "Multiplicity",
                        value: 1,
                        change: false,
                    };
                }
                const entry = numbers.reduce((res, entry) => res.n > entry.n ? res : entry, numbers[0]);
                return {
                    type: "Multiplicity",
                    value: entry.m,
                    change: false,
                };
            })(),
            thickness: (() => {
                const numbers = this.possibleSelections["thickness"].reduce((n, e) => {
                    if (!isManufacturableSinglePart(e)) {
                        throw Error("Wrong type.");
                    }
                    if (e.thickness === undefined) {
                        throw Error("Missing thickness");
                    }
                    const m = n.findIndex(entry => entry.t === e.thickness);
                    if (m === -1) {
                        n.push({
                            t: e.thickness,
                            n: 1,
                        });
                    }
                    else {
                        n[m].n += 1;
                    }
                    return n;
                }, []);
                if (numbers.length === 0) {
                    return {
                        type: "Thickness",
                        value: 0.,
                        change: false,
                    };
                }
                const thicknessEntry = numbers.reduce((res, entry) => res.n > entry.n ? res : entry, numbers[0]);
                return {
                    type: "Thickness",
                    value: thicknessEntry.t,
                    change: false,
                };
            })(),
        };
    }
    get noChanges() {
        return this.types.every(t => !this.currentValues[t].change);
    }
    get currentValue() {
        return this.currentValues[this.currentStep].value;
    }
    get currentDisabled() {
        return !this.currentValues[this.currentStep].change;
    }
    created() {
        this.currentValues = clone(this.defaultValues, isCurrentValues);
    }
    mounted() {
        this.currentValues = clone(this.defaultValues, isCurrentValues);
        const s = this.types.find(t => !this.isDisabled(t));
        if (s !== undefined) {
            this.currentStep = s;
        }
    }
    onCurrentStepChanged() {
        this.invalidValue = this.computeInvalidValue();
    }
    onCurrentValueChanged() {
        this.invalidValue = this.computeInvalidValue();
    }
};
__decorate([
    Prop({ required: true })
], QuickActionsDialog.prototype, "dialogData", void 0);
__decorate([
    Prop({ required: true })
], QuickActionsDialog.prototype, "shopConfig", void 0);
__decorate([
    Inject()
], QuickActionsDialog.prototype, "networkBroker", void 0);
__decorate([
    Inject()
], QuickActionsDialog.prototype, "i18n", void 0);
__decorate([
    Emit()
], QuickActionsDialog.prototype, "close", null);
__decorate([
    Watch("currentStep")
], QuickActionsDialog.prototype, "onCurrentStepChanged", null);
__decorate([
    Watch("currentValues")
], QuickActionsDialog.prototype, "onCurrentValueChanged", null);
QuickActionsDialog = __decorate([
    Component({
        components: {
            GrindingForm,
            ModalBase,
            MaterialForm,
            MultiplicityForm,
            RotationForm,
            ThicknessForm,
        },
        name: "QuickActionsDialog",
    })
], QuickActionsDialog);
export default QuickActionsDialog;
